import {
  Box,
  Center,
  Container,
  Flex,
  Heading,
  HStack,
  Stack,
  Text,
} from '@chakra-ui/react'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { EmphasisText } from '@/components/atoms/Text/EmphasisText'
import { useStepWizard } from '@/components/wizard/StepWizardContext'

import { useColorModeValue } from '../ui/color-mode'

/** Chakra palette token as CSS variable (e.g. green.50 → var(--chakra-colors-green-50)). */
const chakraColor = (token: string) =>
  `var(--chakra-colors-${token.replaceAll('.', '-')})`

const SHELL_GRADIENT_LIGHT = `linear-gradient(200deg, ${chakraColor('green.50')} 0%, ${chakraColor('brand.300')} 32%, ${chakraColor('gray.200')} 72%, ${chakraColor('brand.300')} 100%)`

const SHELL_GRADIENT_DARK = `linear-gradient(210deg, ${chakraColor('gray.900')} 0%, ${chakraColor('pink.100')} 12%, ${chakraColor('gray.600')} 28%, ${chakraColor('gray.800')} 67%, ${chakraColor('gray.950')} 100%)`

// Colorful blue variant (e.g. community wizard) — more saturated, less gray.
const SHELL_GRADIENT_BLUE_LIGHT = `linear-gradient(200deg, ${chakraColor('blue.50')} 0%, ${chakraColor('blue.200')} 30%, ${chakraColor('cyan.200')} 65%, ${chakraColor('blue.300')} 100%)`

const SHELL_GRADIENT_BLUE_DARK = `linear-gradient(210deg, ${chakraColor('gray.900')} 0%, ${chakraColor('blue.400')} 14%, ${chakraColor('blue.800')} 33%, ${chakraColor('gray.800')} 70%, ${chakraColor('gray.950')} 100%)`

interface ProgressIndicatorProps {
  totalSteps: number
  currentStep: number
}

const ProgressIndicator = (props: ProgressIndicatorProps) => {
  const { totalSteps, currentStep } = props
  const stepsArray = Array.from({ length: totalSteps }, (_, i) => i + 1)
  return (
    <HStack>
      {stepsArray.map((step) => (
        <Box
          key={step}
          bg={
            currentStep === step ? 'colorPalette.solid' : 'colorPalette.muted'
          }
          w={currentStep === step ? '10' : '6'}
          transition="all"
          h="1.5"
        />
      ))}
    </HStack>
  )
}

interface StepperImageDiptychLayoutProps {
  title: string
  description: string
  leftElement: ReactNode
  rightElement: ReactNode
  /** Center the right pane content (e.g. preview card); default fills the column. */
  rightContentAlignment?: 'stretch' | 'center'
  /**
   * Gradient on the outer shell and right pane (e.g. network wizard).
   * Left column: white (light) / gray.800 (dark).
   */
  shellGradient?: boolean
  /**
   * Gradient palette when `shellGradient` is on:
   * `green` (default, gray–green, network) or `blue` (colorful, community).
   */
  shellGradientVariant?: 'green' | 'blue'
}

export const StepperDiptychLayout = (props: StepperImageDiptychLayoutProps) => {
  const { totalSteps, currentStep } = useStepWizard()
  const { t } = useTranslation()
  const {
    title,
    description,
    leftElement,
    rightElement,
    rightContentAlignment = 'stretch',
    shellGradient = false,
    shellGradientVariant = 'green',
  } = props

  const leftPaneSolidBg = useColorModeValue('white', 'gray.800')
  const leftIntroTextColor = useColorModeValue('gray.600', 'white')
  const shellGreenGrayGradient = useColorModeValue(
    SHELL_GRADIENT_LIGHT,
    SHELL_GRADIENT_DARK
  )
  const shellBlueGradient = useColorModeValue(
    SHELL_GRADIENT_BLUE_LIGHT,
    SHELL_GRADIENT_BLUE_DARK
  )
  const shellGradientCss =
    shellGradientVariant === 'blue' ? shellBlueGradient : shellGreenGrayGradient

  return (
    <Box minH="full" bg={shellGradient ? shellGradientCss : undefined}>
      <Flex
        minH={{ base: 'auto', lg: 'min(920px, calc(100dvh - 5rem))' }}
        w="full"
        align="stretch"
      >
        <Container
          maxW="2xl"
          display="flex"
          flex="1"
          flexDirection="column"
          bg={leftPaneSolidBg}
        >
          <Stack gap="8" flex="1" pt="10">
            <Stack>
              {currentStep > 0 && currentStep <= totalSteps && (
                <Text fontWeight="medium" textStyle="xs" color="fg.muted">
                  {t('stepper.step', {
                    currentStep,
                    totalSteps,
                  })}
                </Text>
              )}
              <Heading size="5xl">{title}</Heading>
              <EmphasisText color={leftIntroTextColor}>
                {description}
              </EmphasisText>
            </Stack>
            {leftElement}
          </Stack>
          {currentStep > 0 && currentStep <= totalSteps && (
            <Center py="6">
              <ProgressIndicator
                totalSteps={totalSteps}
                currentStep={currentStep}
              />
            </Center>
          )}
        </Container>
        {rightContentAlignment === 'center' ? (
          <Box
            flex="1"
            maxW="4xl"
            hideBelow="lg"
            alignSelf="stretch"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minH={0}
            overflow="auto"
          >
            {rightElement}
          </Box>
        ) : (
          <Box flex="1" maxW="4xl" hideBelow="lg" alignSelf="stretch" minH={0}>
            {rightElement}
          </Box>
        )}
      </Flex>
    </Box>
  )
}
